// src/pages/ForgotPasswordPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Forgot Password — 4-step OTP flow (identical design to LoginPage)
//
//  Step 1 — email       → POST /auth/password/reset/          → sends OTP to email
//  Step 2 — OTP verify  → POST /auth/password/reset/verify/   → validates code
//  Step 3 — new password→ POST /auth/password/reset/confirm/  → sets new password
//  Step 4 — success     → show confirmation + link to login
//
//  All endpoints have fallback patterns for dj-rest-auth / djoser / allauth.
//  Full i18n (en/fr/ar/de), RTL, dark mode, reduced motion, responsive.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import en from "../i18n/translations/en.json";
import ar from "../i18n/translations/ar.json";
import fr from "../i18n/translations/fr.json";
import de from "../i18n/translations/de.json";

const API_BASE         = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";
const STORAGE_LANG_KEY = "site_lang";
const OTP_LENGTH       = 6;
const OTP_EXPIRY_SECS  = 120; // 2 min countdown

// ── i18n ─────────────────────────────────────────────────────────────────────
function useT() {
  const [lang] = useState(() => localStorage.getItem(STORAGE_LANG_KEY) || "en");
  const translations = useMemo(() => {
    const bundle = { en, ar, fr, de };
    const sel = bundle[lang] || en;
    return sel.default ? sel.default : sel;
  }, [lang]);

  const t = useCallback((key, fallback = "") => {
    if (!key) return fallback;
    if (Object.prototype.hasOwnProperty.call(translations, key) && typeof translations[key] === "string")
      return translations[key];
    const parts = key.split(".");
    let cur = translations;
    for (const p of parts) {
      if (cur && typeof cur === "object" && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
      else return fallback || key;
    }
    return typeof cur === "string" ? cur : fallback || key;
  }, [translations]);

  return { t, lang, isRTL: lang === "ar" };
}

// ── helpers ───────────────────────────────────────────────────────────────────
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function pad2(n) { return String(n).padStart(2, "0"); }

function fmtTime(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${pad2(m)}:${pad2(s)}`;
}

// (no generic endpoint-fallback helper needed — exact URLs are used directly)

function extractServerError(err) {
  const d = err?.response?.data;
  if (!d) return null;
  return (
    d.detail       ||
    d.message      ||
    d.error        ||
    d.otp?.[0]     ||
    d.token?.[0]   ||
    d.email?.[0]   ||
    d.new_password?.[0] ||
    d.non_field_errors?.[0] ||
    null
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  STEP COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

// ── Step 1: Email input ───────────────────────────────────────────────────────
function StepEmail({ t, onNext }) {
  const [email,  setEmail]  = useState("");
  const [err,    setErr]    = useState("");
  const [srvErr, setSrvErr] = useState("");
  const [status, setStatus] = useState("idle");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const validate = (v) => {
    if (!v.trim())           return t("email_required",  "Email is required.");
    if (!emailRe.test(v))   return t("email_invalid",   "Enter a valid email address.");
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate(email);
    if (e2) { setErr(e2); return; }

    setStatus("loading"); setSrvErr("");
    try {
      // POST /api/auth/password/reset/  →  request_reset_code view
      await axios.post("/auth/password/reset/", { email: email.trim() });
      setStatus("idle");
      onNext(email.trim());
    } catch (err) {
      const msg  = extractServerError(err);
      const key  = !msg                              ? "reset_failed"
        : /not found|no account/i.test(msg)          ? "email_not_found"
        : /throttl|rate/i.test(msg)                  ? "too_many_requests"
        : /network/i.test(msg)                       ? "network_error"
        : "reset_failed";
      setSrvErr(t(key) || t("reset_failed", "Something went wrong. Please try again."));
      setStatus("error");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="fp-form" noValidate>
      <div className="fp-group">
        <label htmlFor="fp-email" className="fp-label">{t("email_label", "Email address")}</label>
        <div className="fp-input-wrap">
          <span className="fp-input-icon" aria-hidden>✉</span>
          <input
            id="fp-email"
            ref={inputRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder={t("email_placeholder", "you@example.com")}
            value={email}
            onChange={e => { setEmail(e.target.value); if (err) setErr(""); if (srvErr) setSrvErr(""); if (status==="error") setStatus("idle"); }}
            onBlur={() => setErr(validate(email))}
            disabled={status === "loading"}
            className={`fp-input ${err ? "error" : ""}`}
            aria-invalid={!!err}
            aria-describedby={err ? "fp-email-error" : undefined}
          />
        </div>
        {err && <div className="fp-field-error" id="fp-email-error" role="alert"><span aria-hidden>⚠</span> {err}</div>}
      </div>

      {srvErr && (
        <div className={`fp-alert fp-alert--error${status==="error" ? " shake" : ""}`} role="alert" aria-live="assertive">
          <span className="fp-alert-icon" aria-hidden>⚠</span> {srvErr}
        </div>
      )}

      <button
        type="submit"
        className={`fp-btn fp-btn--primary ${status}`}
        disabled={status === "loading"}
        aria-busy={status === "loading"}
      >
        <span className="fp-btn-content">
          {status === "loading" && <span className="fp-spinner" aria-hidden />}
          {status === "loading"
            ? t("sending", "Sending…")
            : t("send_otp_code", "Send verification code")
          }
        </span>
      </button>

      <Link to="/login" className="fp-back-link">← {t("back_to_login", "Back to login")}</Link>
    </form>
  );
}

// ── Step 2: OTP verification ──────────────────────────────────────────────────
function StepOTP({ t, email, onNext, onResend }) {
  const [digits,   setDigits]   = useState(Array(OTP_LENGTH).fill(""));
  const [err,      setErr]      = useState("");
  const [srvErr,   setSrvErr]   = useState("");
  const [status,   setStatus]   = useState("idle");
  const [resendSt, setResendSt] = useState("idle");  // idle | loading | sent
  const [countdown, setCountdown] = useState(OTP_EXPIRY_SECS);
  const boxRefs  = useRef([]);
  const timerRef = useRef(null);

  // Start countdown
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    boxRefs.current[0]?.focus();
    return () => clearInterval(timerRef.current);
  }, []);

  // ── single digit input logic
  const handleDigitChange = (i, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next  = [...digits];
    next[i]     = digit;
    setDigits(next);
    setErr(""); setSrvErr("");
    if (digit && i < OTP_LENGTH - 1) boxRefs.current[i + 1]?.focus();
  };

  const handleDigitKey = (i, e) => {
    if (e.key === "Backspace") {
      if (!digits[i] && i > 0) {
        const next = [...digits]; next[i - 1] = "";
        setDigits(next);
        boxRefs.current[i - 1]?.focus();
      } else {
        const next = [...digits]; next[i] = "";
        setDigits(next);
      }
    } else if (e.key === "ArrowLeft"  && i > 0)              boxRefs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < OTP_LENGTH - 1) boxRefs.current[i + 1]?.focus();
  };

  const handleDigitPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    const next   = [...digits];
    [...pasted].forEach((ch, idx) => { if (idx < OTP_LENGTH) next[idx] = ch; });
    setDigits(next);
    const focus = Math.min(pasted.length, OTP_LENGTH - 1);
    boxRefs.current[focus]?.focus();
  };

  const otp = digits.join("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length < OTP_LENGTH) { setErr(t("otp_incomplete", "Please enter the full 6-digit code.")); return; }

    setStatus("loading"); setSrvErr("");
    try {
      // POST /api/auth/password/reset/verify/  →  verify_reset_code view
      // Django view expects: { email, code }
      const res = await axios.post("/auth/password/reset/verify/", { email, code: otp });
      // The view returns { verified: true/false }
      if (res?.data?.verified === false) {
        throw Object.assign(new Error("Invalid code"), { response: { data: { error: "Invalid or expired code" }, status: 400 } });
      }
      // No reset token is returned — we pass the raw OTP to step 3 as the code
      const resetToken = otp;
      setStatus("idle");
      onNext(resetToken);
    } catch (err) {
      const msg = extractServerError(err);
      const key = !msg                                     ? "otp_invalid"
        : /invalid|incorrect|wrong|expired/i.test(msg)    ? "otp_invalid"
        : /throttl|rate/i.test(msg)                       ? "too_many_requests"
        : /network/i.test(msg)                            ? "network_error"
        : "otp_invalid";
      setSrvErr(t(key) || t("otp_invalid", "Invalid or expired code. Please try again."));
      setStatus("error");
      // Shake + clear inputs on wrong OTP
      setDigits(Array(OTP_LENGTH).fill(""));
      setTimeout(() => boxRefs.current[0]?.focus(), 60);
    }
  };

  const handleResend = async () => {
    setResendSt("loading");
    try {
      await onResend();
      setResendSt("sent");
      setCountdown(OTP_EXPIRY_SECS);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown(c => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
      }, 1000);
      setDigits(Array(OTP_LENGTH).fill(""));
      boxRefs.current[0]?.focus();
      setTimeout(() => setResendSt("idle"), 3000);
    } catch {
      setResendSt("idle");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="fp-form" noValidate>

      {/* Email display */}
      <div className="fp-otp-target">
        <span className="fp-otp-target-icon" aria-hidden>✉</span>
        <span>{t("otp_sent_to", "Code sent to")} <strong>{email}</strong></span>
      </div>

      {/* OTP boxes */}
      <div className="fp-group">
        <label className="fp-label">{t("otp_label", "Verification code")}</label>
        <div className="fp-otp-row" aria-label={t("otp_label", "Verification code")}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={el => boxRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              onChange={e => handleDigitChange(i, e.target.value)}
              onKeyDown={e => handleDigitKey(i, e)}
              onPaste={handleDigitPaste}
              disabled={status === "loading"}
              className={`fp-otp-box ${err || srvErr ? "error" : ""} ${d ? "filled" : ""}`}
              aria-label={`${t("digit", "Digit")} ${i + 1}`}
              autoComplete={i === 0 ? "one-time-code" : "off"}
            />
          ))}
        </div>
        {err && <div className="fp-field-error" role="alert"><span aria-hidden>⚠</span> {err}</div>}
      </div>

      {/* Countdown */}
      <div className="fp-otp-timer">
        {countdown > 0 ? (
          <>
            <span className="fp-otp-timer-icon" aria-hidden>⏱</span>
            <span>{t("otp_expires_in", "Code expires in")} <strong className="fp-otp-countdown">{fmtTime(countdown)}</strong></span>
          </>
        ) : (
          <span className="fp-otp-expired">{t("otp_expired", "Code has expired.")}</span>
        )}
      </div>

      {srvErr && (
        <div className={`fp-alert fp-alert--error${status==="error" ? " shake" : ""}`} role="alert" aria-live="assertive">
          <span className="fp-alert-icon" aria-hidden>⚠</span> {srvErr}
        </div>
      )}

      <button
        type="submit"
        className={`fp-btn fp-btn--primary ${status}`}
        disabled={status === "loading" || otp.length < OTP_LENGTH}
        aria-busy={status === "loading"}
      >
        <span className="fp-btn-content">
          {status === "loading" && <span className="fp-spinner" aria-hidden />}
          {status === "loading"
            ? t("verifying", "Verifying…")
            : t("verify_code", "Verify code")
          }
        </span>
      </button>

      {/* Resend */}
      <div className="fp-resend-row">
        <span className="fp-resend-text">{t("didnt_receive", "Didn't receive it?")}</span>
        <button
          type="button"
          className="fp-resend-btn"
          onClick={handleResend}
          disabled={resendSt === "loading" || countdown > 0}
        >
          {resendSt === "sent"    ? t("resent",       "✓ Sent!")
          : resendSt === "loading" ? t("resending",    "Resending…")
          : countdown > 0          ? `${t("resend_in", "Resend in")} ${fmtTime(countdown)}`
          :                          t("resend_code",  "Resend code")}
        </button>
      </div>
    </form>
  );
}

// ── Step 3: New password ──────────────────────────────────────────────────────
function StepNewPassword({ t, email, token, onNext }) {
  const [pw,      setPw]      = useState("");
  const [pw2,     setPw2]     = useState("");
  const [errPw,   setErrPw]   = useState("");
  const [errPw2,  setErrPw2]  = useState("");
  const [srvErr,  setSrvErr]  = useState("");
  const [status,  setStatus]  = useState("idle");
  const [showPw,  setShowPw]  = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const pw1Ref = useRef(null);

  useEffect(() => { pw1Ref.current?.focus(); }, []);

  const strength = useMemo(() => {
    if (!pw) return 0;
    let s = 0;
    if (pw.length >= 8)         s++;
    if (/[A-Z]/.test(pw))       s++;
    if (/[0-9]/.test(pw))       s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;  // 0–4
  }, [pw]);

  const strengthLabel = ["", t("strength_weak","Weak"), t("strength_fair","Fair"), t("strength_good","Good"), t("strength_strong","Strong")][strength];
  const strengthClass = ["", "weak", "fair", "good", "strong"][strength];

  const validatePw  = (v) => {
    if (!v)          return t("password_required",  "Password is required.");
    if (v.length < 8) return t("password_too_short8","Password must be at least 8 characters.");
    return "";
  };
  const validatePw2 = (v) => {
    if (!v)     return t("confirm_required",   "Please confirm your password.");
    if (v !== pw) return t("passwords_mismatch","Passwords do not match.");
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e1 = validatePw(pw); const e2 = validatePw2(pw2);
    setErrPw(e1); setErrPw2(e2);
    if (e1 || e2) return;

    setStatus("loading"); setSrvErr("");
    try {
      // POST /api/auth/password/reset/confirm/  →  reset_password_with_code view
      // Django view expects: { email, code, password, password2 }
      await axios.post("/auth/password/reset/confirm/", {
        email,
        code:      token,   // the 6-digit OTP carried over from step 2
        password:  pw,
        password2: pw2,
      });
      setStatus("idle");
      onNext();
    } catch (err) {
      const msg = extractServerError(err);
      const key = !msg                                     ? "reset_failed"
        : /invalid|expired|token/i.test(msg)              ? "otp_invalid"
        : /password.*common|too simple/i.test(msg)        ? "password_too_common"
        : /network/i.test(msg)                            ? "network_error"
        : "reset_failed";
      setSrvErr(t(key) || t("reset_failed", "Something went wrong. Please try again."));
      setStatus("error");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="fp-form" noValidate>

      {/* New password */}
      <div className="fp-group">
        <label htmlFor="fp-pw" className="fp-label">{t("new_password", "New password")}</label>
        <div className="fp-input-wrap">
          <span className="fp-input-icon" aria-hidden>🔒</span>
          <input
            id="fp-pw"
            ref={pw1Ref}
            type={showPw ? "text" : "password"}
            autoComplete="new-password"
            required
            value={pw}
            onChange={e => { setPw(e.target.value); if (errPw) setErrPw(""); }}
            onBlur={() => setErrPw(validatePw(pw))}
            disabled={status === "loading"}
            className={`fp-input fp-input--pw ${errPw ? "error" : ""}`}
            aria-invalid={!!errPw}
          />
          <button type="button" className="fp-pw-toggle" onClick={() => setShowPw(s => !s)} aria-pressed={showPw}>
            {showPw ? t("Hide","Hide") : t("Show","Show")}
          </button>
        </div>

        {/* Strength bar */}
        {pw && (
          <div className="fp-strength" aria-live="polite">
            <div className="fp-strength-bar">
              {[1,2,3,4].map(lvl => (
                <div key={lvl} className={`fp-strength-seg ${strength >= lvl ? strengthClass : ""}`} />
              ))}
            </div>
            <span className={`fp-strength-label fp-strength-label--${strengthClass}`}>{strengthLabel}</span>
          </div>
        )}

        {errPw && <div className="fp-field-error" role="alert"><span aria-hidden>⚠</span> {errPw}</div>}
      </div>

      {/* Confirm password */}
      <div className="fp-group">
        <label htmlFor="fp-pw2" className="fp-label">{t("confirm_password", "Confirm password")}</label>
        <div className="fp-input-wrap">
          <span className="fp-input-icon" aria-hidden>🔒</span>
          <input
            id="fp-pw2"
            type={showPw2 ? "text" : "password"}
            autoComplete="new-password"
            required
            value={pw2}
            onChange={e => { setPw2(e.target.value); if (errPw2) setErrPw2(""); }}
            onBlur={() => setErrPw2(validatePw2(pw2))}
            disabled={status === "loading"}
            className={`fp-input fp-input--pw ${errPw2 ? "error" : pw2 && pw2===pw ? "match" : ""}`}
            aria-invalid={!!errPw2}
          />
          <button type="button" className="fp-pw-toggle" onClick={() => setShowPw2(s => !s)} aria-pressed={showPw2}>
            {showPw2 ? t("Hide","Hide") : t("Show","Show")}
          </button>
        </div>
        {pw2 && pw2 === pw && !errPw2 && (
          <div className="fp-field-ok" role="status"><span aria-hidden>✓</span> {t("passwords_match","Passwords match")}</div>
        )}
        {errPw2 && <div className="fp-field-error" role="alert"><span aria-hidden>⚠</span> {errPw2}</div>}
      </div>

      {srvErr && (
        <div className={`fp-alert fp-alert--error${status==="error"?" shake":""}`} role="alert" aria-live="assertive">
          <span className="fp-alert-icon" aria-hidden>⚠</span> {srvErr}
        </div>
      )}

      <button
        type="submit"
        className={`fp-btn fp-btn--primary ${status}`}
        disabled={status === "loading"}
        aria-busy={status === "loading"}
      >
        <span className="fp-btn-content">
          {status === "loading" && <span className="fp-spinner" aria-hidden />}
          {status === "loading"
            ? t("saving", "Saving…")
            : t("set_new_password", "Set new password")
          }
        </span>
      </button>
    </form>
  );
}

// ── Step 4: All done ──────────────────────────────────────────────────────────
function StepDone({ t }) {
  return (
    <div className="fp-done" role="status" aria-live="polite">
      <div className="fp-done-icon" aria-hidden>🎉</div>
      <p className="fp-done-title">{t("password_changed", "Password changed!")}</p>
      <p className="fp-done-sub">{t("password_changed_desc", "Your password has been updated successfully. You can now sign in with your new password.")}</p>
      <Link to="/login" className="fp-btn fp-btn--primary" style={{ marginTop: 8 }}>
        {t("go_to_login", "Go to login")}
      </Link>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const STEPS = ["email", "otp", "password", "done"];

const STEP_META = (t) => [
  { key: "email",    label: t("step_enter_email",  "Email"),    icon: "✉" },
  { key: "otp",      label: t("step_verify_code",  "Verify"),   icon: "🔢" },
  { key: "password", label: t("step_new_password", "Password"), icon: "🔒" },
  { key: "done",     label: t("step_done",         "Done"),     icon: "✓"  },
];

export default function ForgotPasswordPage() {
  const { t, lang, isRTL } = useT();

  const [step,       setStep]       = useState(0);  // 0=email, 1=otp, 2=password, 3=done
  const [email,      setEmail]      = useState("");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    axios.defaults.baseURL = API_BASE;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    localStorage.setItem(STORAGE_LANG_KEY, lang);
  }, [isRTL, lang]);

  const goToOTP = (confirmedEmail) => {
    setEmail(confirmedEmail);
    setStep(1);
  };

  const goToPassword = (token) => {
    setResetToken(token);
    setStep(2);
  };

  const goDone = () => setStep(3);

  const handleResend = async () => {
    // POST /api/auth/password/reset/  (same as step 1 — resends the OTP)
    await axios.post("/auth/password/reset/", { email });
  };

  const stepMeta = STEP_META(t);
  const isSuccess = step === 3;
  const isError   = false; // per-step components manage their own error state

  const TITLES = [
    t("forgot_password",     "Forgot password?"),
    t("verify_your_identity","Verify your identity"),
    t("create_new_password", "Create new password"),
    t("all_done",            "All done!"),
  ];
  const SUBS = [
    t("forgot_desc",         "Enter your email and we'll send you a verification code."),
    t("otp_sub",             "Enter the 6-digit code we sent to your email."),
    t("new_pass_sub",        "Choose a strong password for your account."),
    "",
  ];

  return (
    <div className={`fp-page ${isRTL ? "rtl" : ""}`}>
      <style>{CSS}</style>

      <div className="fp-wrapper">
        <div className={`fp-card ${isSuccess ? "success" : ""}`} role="main" aria-labelledby="fp-heading">
          <div className="fp-content">

            {/* Brand */}
            <div className="fp-brand">
              <div className="fp-dot" aria-hidden />
              <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>24News</span>
            </div>

            {/* ── Stepper ── */}
            <div className="fp-stepper" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
              {stepMeta.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className={`fp-step-node ${i < step ? "done" : i === step ? "active" : "upcoming"}`}>
                    <div className="fp-step-bubble" aria-hidden>
                      {i < step ? "✓" : s.icon}
                    </div>
                    <span className="fp-step-name">{s.label}</span>
                  </div>
                  {i < stepMeta.length - 1 && (
                    <div className={`fp-step-line ${i < step ? "done" : ""}`} aria-hidden />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Header */}
            {step < 3 && (
              <header className="fp-header">
                <h1 id="fp-heading" className="fp-title">{TITLES[step]}</h1>
                {SUBS[step] && <p className="fp-sub">{SUBS[step]}</p>}
              </header>
            )}

            {/* Step content */}
            <div key={step} className="fp-step-content">
              {step === 0 && <StepEmail    t={t} onNext={goToOTP} />}
              {step === 1 && <StepOTP      t={t} email={email} onNext={goToPassword} onResend={handleResend} />}
              {step === 2 && <StepNewPassword t={t} email={email} token={resetToken} onNext={goDone} />}
              {step === 3 && <StepDone     t={t} />}
            </div>
          </div>
        </div>

        <p className="fp-legal">
          © {new Date().getFullYear()} {t("news_platform", "Newsroom")}. {t("all_rights_reserved", "All rights reserved.")}
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  CSS
// ══════════════════════════════════════════════════════════════════════════════
const CSS = `
  :root {
    --primary:       #0c3cff;
    --primary-dark:  #0a2dd6;
    --primary-light: #3d5dff;
    --success:       #10b981;
    --error:         #ef4444;
    --warning:       #f59e0b;
    --bg-primary:    #fbfcff;
    --bg-secondary:  #f8fafc;
    --surface:       #ffffff;
    --text-primary:  #061226;
    --text-secondary:#6b7280;
    --border:        #e5e7eb;
    --border-light:  #f3f4f6;
    --radius-sm:     8px;
    --radius-md:     12px;
    --radius-lg:     18px;
    --shadow-lg:     0 20px 60px rgba(10,18,40,0.12);
    --transition:    cubic-bezier(0.2,0.8,0.2,1);
  }

  @media (prefers-color-scheme: dark) {
    :root {
      --bg-primary:    #0c0e14;
      --bg-secondary:  #13151c;
      --surface:       #1a1d27;
      --text-primary:  #f0f0f5;
      --text-secondary:#8891a8;
      --border:        rgba(255,255,255,0.09);
      --border-light:  rgba(255,255,255,0.04);
      --shadow-lg:     0 20px 60px rgba(0,0,0,0.5);
    }
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Page ── */
  .fp-page {
    min-height:      100vh;
background: rgba(255, 255, 255, 0.5); /* 50% transparent black */    display:         flex;
    flex-direction:  column;
    align-items:     center;
    justify-content: center;
    padding:         24px 16px;
    font-family:     -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color:           var(--text-primary);
    overflow-x:      hidden;
    position:        relative;
  }
  .fp-page.rtl { direction: rtl; }

  /* Orbs */
  .fp-page::before, .fp-page::after {
    content: ""; position: fixed; border-radius: 50%; opacity: .04; pointer-events: none; z-index: 0;
  }
  .fp-page::before { width:500px;height:500px;background:var(--primary);top:-200px;right:-200px;animation:fp-float 25s ease-in-out infinite; }
  .fp-page::after  { width:400px;height:400px;background:var(--success);bottom:-150px;left:-150px;animation:fp-float 30s ease-in-out infinite reverse; }

  /* ── Wrapper & Card ── */
  .fp-wrapper { width:100%; max-width:520px; position:relative; z-index:1; animation:fp-slide-up .7s var(--transition) both; }

  .fp-card {
    background:    var(--surface);
    border:        1px solid var(--border);
    border-radius: var(--radius-lg);
    box-shadow:    var(--shadow-lg);
    position:      relative;
    overflow:      hidden;
    transition:    border-color .3s ease;
  }
  .fp-card::before {
    content:"";position:absolute;top:0;left:0;right:0;height:4px;
    background:linear-gradient(90deg,var(--primary),var(--primary-light));
    transition:background .4s ease;
  }
  .fp-card.success::before { background:linear-gradient(90deg,var(--success),#34d399); }
  .fp-card.success { border-color:rgba(16,185,129,.35); }

  .fp-content { padding:40px 40px 36px; }

  /* ── Brand ── */
  .fp-brand { display:flex;align-items:center;gap:12px;margin-bottom:24px; }
  .fp-dot   { width:12px;height:12px;background:linear-gradient(135deg,var(--primary),var(--primary-light));border-radius:50%;animation:fp-pulse 2s ease-in-out infinite; }

  /* ══ STEPPER ══════════════════════════════════════════════ */
  .fp-stepper {
    display:         flex;
    align-items:     flex-start;
    justify-content: space-between;
    margin-bottom:   32px;
    padding-bottom:  24px;
    border-bottom:   1px solid var(--border);
    position:        relative;
  }

  .fp-step-node {
    display:        flex;
    flex-direction: column;
    align-items:    center;
    gap:            8px;
    flex:           1;
    position:       relative;
  }

  .fp-step-bubble {
    width:           38px;
    height:          38px;
    border-radius:   50%;
    display:         flex;
    align-items:     center;
    justify-content: center;
    font-size:       15px;
    font-weight:     700;
    border:          2px solid var(--border);
    background:      var(--bg-secondary);
    color:           var(--text-secondary);
    transition:      all .3s var(--transition);
    position:        relative;
    z-index:         1;
  }

  .fp-step-node.active .fp-step-bubble {
    background:  linear-gradient(135deg,var(--primary),var(--primary-light));
    border-color:transparent;
    color:       #fff;
    box-shadow:  0 4px 16px rgba(12,60,255,.3);
    animation:   fp-pulse-step 2s ease-in-out infinite;
  }

  .fp-step-node.done .fp-step-bubble {
    background:  linear-gradient(135deg,var(--success),#059669);
    border-color:transparent;
    color:       #fff;
    box-shadow:  0 4px 14px rgba(16,185,129,.25);
  }

  .fp-step-name {
    font-size:   11px;
    font-weight: 600;
    color:       var(--text-secondary);
    letter-spacing:.03em;
    text-align:  center;
    white-space: nowrap;
    transition:  color .3s;
  }

  .fp-step-node.active   .fp-step-name { color:var(--primary);   font-weight:700; }
  .fp-step-node.done     .fp-step-name { color:var(--success);   }

  .fp-step-line {
    flex:       1;
    height:     2px;
    background: var(--border);
    margin-top: 18px;
    transition: background .4s ease;
    position:   relative;
    z-index:    0;
  }
  .fp-step-line.done { background:linear-gradient(90deg,var(--success),rgba(16,185,129,.4)); }

  /* ── Header ── */
  .fp-header { margin-bottom:24px; animation:fp-fade-down .45s var(--transition) both; }
  .fp-title  { font-size:24px;font-weight:700;margin-bottom:8px;line-height:1.2;letter-spacing:-.3px; }
  .fp-sub    { font-size:14px;color:var(--text-secondary);line-height:1.6; }

  /* ── Step content enter animation ── */
  .fp-step-content { animation:fp-fade-up .4s var(--transition) both; }

  /* ── Form ── */
  .fp-form   { display:flex;flex-direction:column;gap:18px; }
  .fp-group  { display:flex;flex-direction:column;gap:8px; }
  .fp-label  { font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--text-primary);opacity:.75; }

  /* Input */
  .fp-input-wrap { position:relative;display:flex;align-items:center; }
  .fp-input-icon {
    position:absolute;left:14px;font-size:15px;
    color:var(--text-secondary);pointer-events:none;opacity:.65;line-height:1;
  }
  .fp-page.rtl .fp-input-icon { left:auto;right:14px; }

  .fp-input {
    width:100%;padding:13px 16px 13px 42px;
    background:var(--bg-secondary);border:1.5px solid var(--border);
    border-radius:var(--radius-md);font-size:15px;line-height:1.5;
    font-family:inherit;color:var(--text-primary);transition:all .2s ease;
  }
  .fp-page.rtl .fp-input { padding-left:16px;padding-right:42px; }
  .fp-input::placeholder { color:#b3b9c8; }
  .fp-input:focus { outline:none;background:var(--surface);border-color:var(--primary);box-shadow:0 0 0 3px rgba(12,60,255,.1);transform:translateY(-1px); }
  .fp-input.error { border-color:var(--error);background:rgba(239,68,68,.02); }
  .fp-input.error:focus { box-shadow:0 0 0 3px rgba(239,68,68,.1); }
  .fp-input.match { border-color:var(--success); }

  /* Password input extra padding for toggle */
  .fp-input--pw { padding-right:70px; }
  .fp-page.rtl .fp-input--pw { padding-right:42px;padding-left:70px; }

  /* Password toggle */
  .fp-pw-toggle {
    position:absolute;right:12px;background:none;border:none;
    color:var(--text-secondary);font-size:11px;font-weight:700;cursor:pointer;
    padding:5px 8px;border-radius:5px;transition:all .18s;
    text-transform:uppercase;letter-spacing:.05em;font-family:inherit;
  }
  .fp-page.rtl .fp-pw-toggle { right:auto;left:12px; }
  .fp-pw-toggle:hover { background:var(--border-light);color:var(--text-primary); }

  /* Field messages */
  .fp-field-error { font-size:12px;color:var(--error);display:flex;align-items:center;gap:6px;animation:fp-slide-down .2s ease; }
  .fp-field-ok    { font-size:12px;color:var(--success);display:flex;align-items:center;gap:6px;animation:fp-slide-down .2s ease; }

  /* Server alert */
  .fp-alert {
    padding:12px 14px;border-radius:var(--radius-sm);font-size:13px;font-weight:500;
    display:flex;align-items:center;gap:10px;
  }
  .fp-alert--error {
    background:rgba(239,68,68,.06);border:1px solid rgba(239,68,68,.2);color:#b91c1c;
  }
  .fp-alert.shake { animation:fp-shake .4s ease; }
  .fp-alert-icon  { font-size:15px;flex-shrink:0; }

  /* ══ OTP BOXES ════════════════════════════════════════════ */
  .fp-otp-target {
    display:       flex;
    align-items:   center;
    gap:           10px;
    padding:       12px 14px;
    background:    var(--bg-secondary);
    border:        1px solid var(--border);
    border-radius: var(--radius-sm);
    font-size:     14px;
    color:         var(--text-secondary);
  }
  .fp-otp-target strong { color:var(--text-primary);font-weight:600; }
  .fp-otp-target-icon   { font-size:18px;opacity:.7; }

  .fp-otp-row {
    display:         flex;
    gap:             10px;
    justify-content: center;
  }

  .fp-otp-box {
    width:         48px;
    height:        56px;
    text-align:    center;
    font-size:     22px;
    font-weight:   700;
    border:        2px solid var(--border);
    border-radius: var(--radius-md);
    background:    var(--bg-secondary);
    color:         var(--text-primary);
    caret-color:   var(--primary);
    transition:    all .18s var(--transition);
    outline:       none;
    font-family:   "SF Mono", "Fira Code", monospace, inherit;
  }

  .fp-otp-box:focus {
    border-color: var(--primary);
    box-shadow:   0 0 0 3px rgba(12,60,255,.12);
    background:   var(--surface);
    transform:    translateY(-2px);
  }

  .fp-otp-box.filled {
    border-color: var(--primary);
    background:   rgba(12,60,255,.03);
  }

  .fp-otp-box.error {
    border-color: var(--error);
    background:   rgba(239,68,68,.04);
    animation:    fp-shake .4s ease;
  }

  /* Countdown + resend */
  .fp-otp-timer {
    display:     flex;
    align-items: center;
    justify-content: center;
    gap:         7px;
    font-size:   13px;
    color:       var(--text-secondary);
  }
  .fp-otp-timer-icon { font-size:14px;opacity:.7; }
  .fp-otp-countdown  { font-variant-numeric:tabular-nums;color:var(--primary);font-weight:700; }
  .fp-otp-expired    { color:var(--error);font-weight:600;font-size:13px; }

  .fp-resend-row {
    display:         flex;
    align-items:     center;
    justify-content: center;
    gap:             8px;
    font-size:       13px;
  }
  .fp-resend-text { color:var(--text-secondary); }
  .fp-resend-btn  {
    background:none;border:none;cursor:pointer;
    color:var(--primary);font-weight:700;font-size:13px;font-family:inherit;
    padding:4px 6px;border-radius:5px;transition:all .18s;
  }
  .fp-resend-btn:hover:not(:disabled) { background:rgba(12,60,255,.07); }
  .fp-resend-btn:disabled             { opacity:.5;cursor:not-allowed;color:var(--text-secondary); }

  /* ══ PASSWORD STRENGTH ════════════════════════════════════ */
  .fp-strength { display:flex;align-items:center;gap:10px;margin-top:4px; }

  .fp-strength-bar {
    display:flex;gap:4px;flex:1;
  }
  .fp-strength-seg {
    flex:1;height:4px;border-radius:2px;
    background:var(--border);transition:background .3s ease;
  }
  .fp-strength-seg.weak   { background:#ef4444; }
  .fp-strength-seg.fair   { background:#f59e0b; }
  .fp-strength-seg.good   { background:#3b82f6; }
  .fp-strength-seg.strong { background:#10b981; }

  .fp-strength-label         { font-size:11px;font-weight:700;white-space:nowrap; }
  .fp-strength-label--weak   { color:#ef4444; }
  .fp-strength-label--fair   { color:#f59e0b; }
  .fp-strength-label--good   { color:#3b82f6; }
  .fp-strength-label--strong { color:#10b981; }

  /* ══ BUTTONS ══════════════════════════════════════════════ */
  .fp-btn {
    display:         flex;
    align-items:     center;
    justify-content: center;
    gap:             8px;
    width:           100%;
    padding:         13px 24px;
    border-radius:   var(--radius-md);
    font-size:       15px;
    font-weight:     700;
    letter-spacing:  .03em;
    cursor:          pointer;
    transition:      all .2s ease;
    text-decoration: none;
    text-align:      center;
    font-family:     inherit;
    min-height:      46px;
    border:          none;
  }

  .fp-btn--primary {
    background:linear-gradient(135deg,var(--primary),var(--primary-light));
    color:#fff;box-shadow:0 4px 12px rgba(12,60,255,.2);
  }
  .fp-btn--primary:hover:not(:disabled) { transform:translateY(-2px);box-shadow:0 8px 24px rgba(12,60,255,.3); }
  .fp-btn--primary:active:not(:disabled){ transform:translateY(0); }
  .fp-btn--primary:disabled             { opacity:.6;cursor:not-allowed; }
  .fp-btn--primary.loading              { opacity:.8; }
  .fp-btn--primary.error                { background:linear-gradient(135deg,var(--error),#dc2626);box-shadow:0 4px 12px rgba(239,68,68,.25); }

  .fp-btn--outline {
    background:transparent;color:var(--primary);
    border:1.5px solid rgba(12,60,255,.3);
  }
  .fp-btn--outline:hover { background:rgba(12,60,255,.05);border-color:var(--primary);transform:translateY(-1px); }

  .fp-btn--ghost {
    background:var(--bg-secondary);color:var(--text-secondary);
    border:1.5px solid var(--border);
  }
  .fp-btn--ghost:hover { color:var(--text-primary);background:var(--border-light);transform:translateY(-1px); }

  .fp-btn-content { display:flex;align-items:center;gap:8px; }

  .fp-spinner {
    width:16px;height:16px;
    border:2px solid rgba(255,255,255,.4);border-top-color:#fff;
    border-radius:50%;animation:fp-spin .8s linear infinite;
    display:inline-block;flex-shrink:0;
  }

  .fp-back-link {
    display:flex;align-items:center;justify-content:center;
    font-size:14px;font-weight:600;color:var(--text-secondary);
    text-decoration:none;padding:8px;border-radius:var(--radius-sm);transition:color .18s;
  }
  .fp-back-link:hover { color:var(--primary); }

  /* ══ DONE SCREEN ══════════════════════════════════════════ */
  .fp-done {
    display:        flex;
    flex-direction: column;
    align-items:    center;
    text-align:     center;
    gap:            12px;
    padding:        12px 0;
    animation:      fp-fade-up .5s var(--transition) both;
  }
  .fp-done-icon  { font-size:52px;animation:fp-scale-in .5s .1s var(--transition) both; }
  .fp-done-title { font-size:22px;font-weight:700;color:var(--text-primary); }
  .fp-done-sub   { font-size:14px;color:var(--text-secondary);line-height:1.65;max-width:340px; }

  /* ── Legal ── */
  .fp-legal { margin-top:24px;font-size:12px;color:#a0a8b8;text-align:center;font-weight:500;opacity:.7;animation:fp-fade-up .55s .35s var(--transition) both; }

  /* ══ KEYFRAMES ════════════════════════════════════════════ */
  @keyframes fp-slide-up   { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:none} }
  @keyframes fp-fade-down  { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:none} }
  @keyframes fp-fade-up    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  @keyframes fp-scale-in   { from{opacity:0;transform:scale(.7)} to{opacity:1;transform:scale(1)} }
  @keyframes fp-shake      { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-8px)} 75%{transform:translateX(8px)} }
  @keyframes fp-spin       { to{transform:rotate(360deg)} }
  @keyframes fp-pulse      { 0%,100%{opacity:1} 50%{opacity:.55} }
  @keyframes fp-pulse-step { 0%,100%{box-shadow:0 4px 16px rgba(12,60,255,.3)} 50%{box-shadow:0 4px 22px rgba(12,60,255,.55)} }
  @keyframes fp-float      { 0%,100%{transform:translateY(0)} 50%{transform:translateY(28px)} }
  @keyframes fp-slide-down { from{opacity:0;transform:translateY(-5px)} to{opacity:1;transform:none} }

  @media (prefers-reduced-motion:reduce) { *,*::before,*::after{animation:none!important;transition:none!important;} }

  /* ══ RESPONSIVE ═══════════════════════════════════════════ */
  @media (max-width:520px) {
    .fp-content { padding:28px 20px 24px; }
    .fp-title   { font-size:21px; }
    .fp-otp-box { width:42px;height:50px;font-size:20px; }
    .fp-stepper { gap:0; }
    .fp-step-name { font-size:10px; }
    .fp-step-bubble { width:32px;height:32px;font-size:13px; }
    .fp-step-line { margin-top:15px; }
  }
  @media (max-width:400px) {
    .fp-content { padding:24px 16px 20px; }
    .fp-otp-row { gap:7px; }
    .fp-otp-box { width:38px;height:46px;font-size:18px; }
  }
  @media (max-width:340px) {
    .fp-otp-row { gap:5px; }
    .fp-otp-box { width:34px;height:42px;font-size:17px; }
  }
`;